import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Departments and the categories inside them — the two levels the storefront's
 * menu is built from.
 *
 * The endpoint names do not line up with the words on screen, and there is no
 * fixing that from here, so they are stated once:
 *
 *   department  →  `businesses/supersections` (list), `businesses/supersection`
 *                  (save, singular), `businesses/departments/{id}` (delete)
 *   category    →  `businesses/menusections` (list, save and delete)
 *
 * Categories save through one POST for both create and update; departments use
 * POST to create and PUT to update.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** Both levels share these. `name` is Spanish; `nameEng` is English. */
interface MenuNode {
  id: string;
  name: string | null;
  nameEng: string | null;
  imageUrl: string | null;
  /** Hex, and the storefront draws the tile with them. */
  color: string | null;
  borderColor: string | null;
  ordering: number | null;
}

export type Department = MenuNode;

export interface Category extends Omit<MenuNode, 'name'> {
  /** The category list names its Spanish field `nameSpa`, not `name`. */
  nameSpa: string | null;
  superSectionId: string | null;
  superSectionName: string | null;
  superSectionNameEng: string | null;
  superSectionImageUrl: string | null;
  aisle: string | null;
  isAlcohol: boolean;
}

// ------------------------------------------------------------ departments

export function useDepartments() {
  return useQuery({
    queryKey: ['catalog', 'departments'],
    queryFn: () => get<{ results: Department[] }>('businesses/supersections'),
    select: (data) => data.results ?? [],
  });
}

function useInvalidateCatalog() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['catalog'] });
}

export type DepartmentInput = Omit<Department, 'id'> & { id?: string };

export function useSaveDepartment() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: (body: DepartmentInput) =>
      unwrap<unknown>(
        body.id
          ? api.put('businesses/supersection', body)
          : api.post('businesses/supersection', body)
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteDepartment() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`businesses/departments/${id}`)),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------- categories

export function useCategories() {
  return useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => get<{ results: Category[] }>('businesses/menusections'),
    select: (data) => data.results ?? [],
  });
}

export type CategoryInput = Omit<Category, 'id'> & { id?: string };

/** One POST for both create and update — the API keys off the body's `id`. */
export function useSaveCategory() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: (body: CategoryInput) =>
      unwrap<unknown>(api.post('businesses/menusections', body)),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`businesses/menusections/${id}`)),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------------- logs

export interface CatalogLog {
  id?: string;
  createdAt: string | null;
  message: string | null;
}

/**
 * The change history of one department or category.
 *
 * Two endpoints with one shape, chosen by which id is given — the original
 * built the URL inline in the modal, which is why the two could not be told
 * apart from the outside.
 */
export function useCatalogLogs({
  departmentId,
  categoryId,
  page,
  size,
}: {
  departmentId?: string | null;
  categoryId?: string | null;
  page: number;
  size: number;
}) {
  const id = categoryId ?? departmentId;
  const path = categoryId ? 'sections' : 'supersections';

  return useQuery({
    queryKey: ['catalog', 'logs', path, id, page, size],
    queryFn: () =>
      get<{ logs: CatalogLog[]; count: number }>(
        `admin/${path}/${id}?page=${page}&pageSize=${size}`
      ),
    enabled: !!id,
  });
}

// ----------------------------------------------------------------- upload

/** The same uploader the campaign images use; it returns the stored URL. */
export function useUploadCatalogImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('utility/productcampaign/uploadfile', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
